/**
 * Inline edit panel for a submitter on their own ticket (while status is
 * still New or In Progress). Allows editing title, description, and
 * severity/priority. A Save Changes button appears when the form is dirty.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select,
  TextField, Typography,
} from '@mui/material';
import { Edit, Save } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { bugSeverities, featurePriorities } from '../../constants/statusOptions';
import { updateMyTicket } from '../../services/feedbackService';
import type { FeedbackTicketDetail } from '../../types/feedback';

interface Props {
  ticket: FeedbackTicketDetail;
  onSaved: () => void;
}

export const SubmitterEditPanel = ({ ticket, onSaved }: Props) => {
  const theme = useTheme();
  const [title, setTitle] = useState(ticket.Title);
  const [description, setDescription] = useState(ticket.Description || '');
  const [severity, setSeverity] = useState(ticket.Severity || 'Medium');
  const [priority, setPriority] = useState(ticket.Priority || 'Nice to Have');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset draft when ticket changes underneath (after save refresh)
  useEffect(() => {
    setTitle(ticket.Title);
    setDescription(ticket.Description || '');
    setSeverity(ticket.Severity || 'Medium');
    setPriority(ticket.Priority || 'Nice to Have');
  }, [ticket]);

  const dirty =
    title.trim() !== ticket.Title ||
    description !== (ticket.Description || '') ||
    (ticket.Type === 'bug' && severity !== (ticket.Severity || 'Medium')) ||
    (ticket.Type === 'feature' && priority !== (ticket.Priority || 'Nice to Have'));

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      await updateMyTicket(ticket.FeedbackTicketKey, {
        Title: title,
        Description: description.trim() || null,
        Severity: ticket.Type === 'bug' ? severity : undefined,
        Priority: ticket.Type === 'feature' ? priority : undefined,
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [ticket, title, description, severity, priority, onSaved]);

  return (
    <Paper variant="outlined" sx={{
      p: 2, mb: 2.5, borderRadius: 1.5,
      borderLeft: `4px solid ${theme.palette.info.main}`,
      bgcolor: alpha(theme.palette.info.main, 0.02),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Edit sx={{ fontSize: 18, color: 'info.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'info.main' }}>
          Edit Report
        </Typography>
        <Chip label="Editable" size="small" sx={{
          height: 18, fontSize: '0.65rem', fontWeight: 600,
          bgcolor: alpha(theme.palette.info.main, 0.12), color: 'info.main',
        }} />
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', ml: 0.5 }}>
          You can edit this while it's in {ticket.Status}
        </Typography>
      </Box>

      <TextField
        label="Title"
        fullWidth size="small"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        inputProps={{ maxLength: 200 }}
        sx={{ mb: 1.5 }}
      />

      <TextField
        label="Description"
        fullWidth multiline rows={5} size="small"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={ticket.Type === 'bug'
          ? 'Steps to reproduce, expected vs. actual behaviour...'
          : 'What the feature should do and why...'}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {ticket.Type === 'bug' && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Severity</InputLabel>
            <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value)}>
              {bugSeverities.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {ticket.Type === 'feature' && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value)}>
              {featurePriorities.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <Box sx={{ flex: 1 }} />
        {error && <Typography variant="caption" color="error">{error}</Typography>}
        <Button
          variant="contained" size="small" startIcon={<Save sx={{ fontSize: 16 }} />}
          disabled={!dirty || saving || !title.trim()}
          onClick={handleSave}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Paper>
  );
};
